import { useMemo, useState } from 'react';

import { CalendarBlankIcon, CheckCircleIcon, XCircleIcon } from '@phosphor-icons/react/dist/ssr';

import { ButtonElement } from '@/subscriptionManagementElements/shared/buttonElement/ButtonElement';
import type { Fulfillment, OrderItemData } from '@/types';
import { dateFormatter } from '@/utils/dateFormatter';
import { timeFormatter } from '@/utils/timeFormatter';

import OrderItemReview from './OrderItemReview';

interface Props {
  orderItem: OrderItemData;
  deliveryDate: string;
  translations?: Record<string, string>;
  onAccessFulfillment: (orderItem: OrderItemData, fulfillment: Fulfillment) => Promise<null>;
  onAddToCalendar?: (fulfillmentId: string) => void;
  onClickReviewProduct?: () => void;
  hidePrice?: boolean;
}

export const OrderItem = ({
  orderItem,
  translations,
  deliveryDate,
  onAccessFulfillment,
  onAddToCalendar,
  onClickReviewProduct,
  hidePrice = false,
}: Props) => {
  const [isAccessing, setIsAccessing] = useState(false);

  const fulfillment = orderItem.fulfillment;
  const isWebinar = orderItem.product_variant.type === 'webinar';
  const webinarStatus = orderItem.product_variant.redeemable_metadata?.status;
  const isWebinarCancelled = isWebinar && (fulfillment?.status === 'cancelled' || webinarStatus === 'cancelled');
  const isCancelled = fulfillment?.status === 'cancelled' || isWebinarCancelled;
  const isCompleted = fulfillment?.status === 'completed';

  const handleAccess = async () => {
    if (!fulfillment || isCancelled) return;

    setIsAccessing(true);

    try {
      await onAccessFulfillment(orderItem, fulfillment);
    } catch {
      // Handled in callback
    } finally {
      setIsAccessing(false);
    }
  };

  const coachingSessionStartDateTime = fulfillment?.metadata?.cal_booking_start_time
    ? new Date(fulfillment?.metadata?.cal_booking_start_time)
    : null;
  const coachingSessionMeetingUrl = fulfillment?.metadata?.cal_booking_meeting_url;

  const metadata = orderItem.product_variant.redeemable_metadata;
  const webinarEventStartsAt = metadata?.event_starts_at ? new Date(metadata.event_starts_at) : null;
  const webinarEventEndsAt = metadata?.event_ends_at ? new Date(metadata.event_ends_at) : null;
  const webinarTimeZoneKey = metadata?.time_zone ?? undefined;
  const webinarTimeZoneIana = metadata?.time_zone_iana ?? webinarTimeZoneKey;
  const hasWebinarDetails = isWebinar && webinarEventStartsAt;

  const isCTAHidden = orderItem.hide_access_fulfillment_button;
  const isCTADisabled = useMemo(() => {
    return !fulfillment || isAccessing || !fulfillment?.can_access;
  }, [fulfillment, isAccessing]);

  const hasCoachingSessionDetails =
    !isCTAHidden && isCompleted && coachingSessionStartDateTime && coachingSessionMeetingUrl;

  const isWebinarOver = !!webinarEventEndsAt && Date.now() > webinarEventEndsAt.getTime();
  const isWebinarUnavailable = isWebinar && (isWebinarCancelled || isWebinarOver);

  const showAddToCalendar = isWebinar && fulfillment && onAddToCalendar && !isWebinarUnavailable;

  return (
    <div key={orderItem.id} className="p-5 flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row gap-6">
        <img
          src={orderItem.product_variant.thumbnail_url}
          className="aspect-[16/9] sm:w-[160px] sm:h-[90px] rounded-md object-cover"
          alt="Order item"
        />
        <div className="flex flex-col gap-1 w-full min-w-0">
          <div className="flex flex-col sm:flex-row justify-between items-center w-full">
            <h5 className="text-gray-900 text-sm font-medium leading-5 w-full sm:w-auto">
              {orderItem.product_variant.name}
            </h5>
            {!hidePrice && (
              <p className="text-gray-900 font-medium text-sm leading-5 w-full sm:w-auto">
                {orderItem.free ? translations?.['manage.orders.order.free'] || 'Free' : orderItem.price}
              </p>
            )}
          </div>
          {orderItem.product_description && (
            <p className="text-gray-500 text-sm font-normal leading-5 w-full sm:w-auto break-words">
              {orderItem.product_description}
            </p>
          )}
          {hasCoachingSessionDetails && (
            <div className="flex flex-col mt-2">
              <p className="text-gray-900 text-sm font-medium leading-5 mb-1">
                {translations?.['manage.orders.order.booking.meeting_details'] || 'Meeting Details'}
              </p>
              {coachingSessionStartDateTime && (
                <p className="text-gray-600 text-sm font-normal leading-5">
                  {dateFormatter({}).format(coachingSessionStartDateTime)}{' '}
                  {timeFormatter({ timeStyle: 'short' }).format(coachingSessionStartDateTime)}
                </p>
              )}
              {coachingSessionMeetingUrl && (
                <div className="flex flex-row items-center gap-2">
                  <span className="text-gray-600 text-sm font-normal leading-5">
                    {translations?.['manage.orders.order.booking.video_call_link']}
                  </span>
                  <a
                    href={coachingSessionMeetingUrl}
                    className="underline text-sm font-normal leading-5 text-var(--pt-text-on-background-color)"
                  >
                    {coachingSessionMeetingUrl}
                  </a>
                </div>
              )}
            </div>
          )}
          {hasWebinarDetails && (
            <div className="flex flex-col mt-2">
              <p className="text-gray-900 text-sm font-medium leading-5 mb-1">
                {translations?.['manage.orders.order.webinar.event_details'] || 'Event details'}
              </p>
              {webinarEventStartsAt && (
                <p className="text-gray-600 text-sm font-normal leading-5">
                  {dateFormatter({ timeZone: webinarTimeZoneIana }).format(webinarEventStartsAt)}{' '}
                  {timeFormatter({ timeStyle: 'short', timeZone: webinarTimeZoneIana }).format(webinarEventStartsAt)}
                  {webinarEventEndsAt &&
                    ` – ${timeFormatter({ timeStyle: 'short', timeZone: webinarTimeZoneIana }).format(
                      webinarEventEndsAt,
                    )}`}
                  {webinarTimeZoneKey && ` (${webinarTimeZoneKey})`}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      {orderItem.product_review && (
        <OrderItemReview
          review={orderItem.product_review.review ?? undefined}
          score={orderItem.product_review.score}
          timestamp={orderItem.product_review.updated_at}
          translations={translations}
        />
      )}
      <div className="flex flex-col gap-y-2 sm:flex-row justify-between items-center">
        <span className="w-full sm:w-auto flex flex-row items-center gap-2">
          {isWebinarCancelled ? (
            <>
              <XCircleIcon width="20" height="20" weight="fill" className="text-amber-500" />
              <p className="text-sm leading-5 text-gray-500 font-medium">
                {translations?.['manage.orders.order.webinar.cancelled'] || 'Event cancelled'}
              </p>
            </>
          ) : (
            <>
              <CheckCircleIcon width="20" height="20" weight="fill" className="text-green-500" />
              <p className="text-sm leading-5 text-gray-500 font-medium">
                {translations?.['manage.orders.delivered_on']} {dateFormatter({}).format(new Date(deliveryDate))}
              </p>
            </>
          )}
        </span>
        <div className="flex flex-row flex-wrap items-center gap-2">
          {!isCTAHidden && !isWebinarUnavailable && (
            <ButtonElement
              variant={isCancelled ? 'default' : 'primary'}
              onClick={handleAccess}
              disabled={isCTADisabled}
              className="w-full sm:w-auto"
            >
              {isAccessing
                ? translations?.['manage.orders.order.cta.accessing'] || 'Accessing...'
                : isCancelled
                  ? translations?.['manage.orders.order.cta.cancelled'] || 'Cancelled'
                  : translations?.[`manage.orders.order.cta.${orderItem.product_variant.type}`] || 'Access Product'}
            </ButtonElement>
          )}
          {showAddToCalendar && (
            <button
              type="button"
              onClick={() => onAddToCalendar(fulfillment.id)}
              className="inline-flex items-center justify-center border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 px-3 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-opacity rounded-md font-sans w-full sm:w-auto"
            >
              <CalendarBlankIcon className="h-4 w-4 mr-2" aria-hidden="true" />
              {translations?.['manage.orders.order.cta.add_to_calendar'] ?? 'Add to calendar'}
            </button>
          )}
          {onClickReviewProduct && (
            <ButtonElement variant="default" onClick={() => onClickReviewProduct()}>
              {orderItem.product_review?.id
                ? translations?.['manage.orders.order.cta.edit_product_review'] || 'Edit review'
                : translations?.['manage.orders.order.cta.create_product_review'] || 'Review'}
            </ButtonElement>
          )}
        </div>
      </div>
    </div>
  );
};
